// Per-resource permissions of an API key (spec 2026-10-20 §10; contract
// §2.3, §5). Core holds the catalogue of resource groups and judges every
// grant; content serves the groups a custom key can be given here (GET
// /api-key/resources: Groups, Group membership, Courses, and AI tools
// telemetry only on EducationXR's own organization). These words only shape
// what the grid offers and say what a key holds.

// The resource groups only EducationXR staff may give or rotate (core's
// catalogue marks them platform_only: AI tools telemetry). The resource list
// that says so is staff's to read, so the admin names them here to know,
// from any key's own grants, that a rotation would be refused to everyone
// else (403 api_key.grants_staff_only).
export const PLATFORM_ONLY_GROUPS = ['ai_tools_telemetry']

// Whether the key holds a platform-only group above No access. A key whose
// grants are not known (a view without them) holds none as far as we can say.
export const holdsPlatformOnly = key => {
  const grants = key && key.grants
  return !!grants && PLATFORM_ONLY_GROUPS.some(id => !!grants[id] && grants[id] !== 'none')
}

// The create form's "Custom permissions" choice beside the preset roles.
export const CUSTOM_CHOICE = 'custom'
export const CUSTOM_HELP = 'Choose what the key can do with each resource. Only EducationXR staff can make one.'

// A custom key's levels. "Read and write" includes deleting (ledger R2:
// GitHub's and Stripe's meaning); a key that must create and change but
// never delete uses the API read-write preset.
export const LEVELS = [
  { value: 'none', label: 'No access' },
  { value: 'read', label: 'Read' },
  { value: 'read_write', label: 'Read and write' }
]

// "partial" is a preset's level that the grid can't give: create, read and
// change, never delete (the API read-write preset, contract §5).
const LEVEL_WORDS = { none: 'No access', read: 'Read', read_write: 'Read and write', partial: 'Read, create and change' }
export const levelText = level => LEVEL_WORDS[level] || LEVEL_WORDS.none

// The levels a group offers: always No access, then those the server lists.
export const levelsFor = group =>
  LEVELS.filter(level => level.value === 'none' || (!!group && Array.isArray(group.levels) && group.levels.includes(level.value)))

// A group the server's list doesn't name (a preset reaches groups a custom
// key can't be given yet: People, Course assignments, Analytics...), in
// words from its id: "course_assignments" -> "Course assignments".
export const groupWords = id => {
  const text = String(id || '').replace(/_/g, ' ').trim()
  return text ? text[0].toUpperCase() + text.slice(1) : ''
}

// "A", "A and B", "A, B and C".
export const listText = items => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`)

// The rows of a key's Permissions section, { id, label, level }: every group
// offered here, in the server's order (its level, No access included), then
// any other group the key's role reaches, by name (the view's map has no
// order of its own).
export const grantRows = (grants, groups) => {
  const held = grants || {}
  const offered = (groups || []).map(group => ({ id: group.id, label: group.label, level: held[group.id] || 'none' }))
  const others = Object.entries(held)
    .filter(([id, level]) => level && level !== 'none' && !offered.some(row => row.id === id))
    .map(([id, level]) => ({ id, label: groupWords(id), level }))
    .sort((a, b) => a.label.localeCompare(b.label))
  return [...offered, ...others]
}

// Where the grid starts for a key: what it holds, never wider. A preset's
// "partial" starts at Read: the grid can't say create-and-change without
// delete, and must never widen a key on its own.
export const startingGrants = (grants, groups) => Object.fromEntries((groups || []).map(group => {
  const held = (grants || {})[group.id]
  const offered = levelsFor(group).map(level => level.value)
  if (held === 'read_write' && offered.includes('read_write')) return [group.id, 'read_write']
  if (['read', 'partial', 'read_write'].includes(held) && offered.includes('read')) return [group.id, 'read']
  return [group.id, 'none']
}))

// The offered groups a preset gives "partial" access, by label.
export const narrowedGroups = (grants, groups) => (groups || []).filter(group => (grants || {})[group.id] === 'partial').map(group => group.label)

// A grid with nothing above No access is no key at all.
export const grantsProblem = grants =>
  (Object.values(grants || {}).some(level => level && level !== 'none') ? null : 'Give the key access to at least one resource.')

// What's sent: only the groups above No access (an omitted group is none).
export const grantsBody = grants => Object.fromEntries(Object.entries(grants || {}).filter(([, level]) => level && level !== 'none'))
